import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { referralKeys } from '@/api/queries/referrals/key';
import {
  applyReferralSubscriptionCredit,
  withdrawReferralEarnings,
} from './request';
import type {
  ReferralSubscriptionCreditPayload,
  ReferralWithdrawalPayload,
} from './payload';

const invalidateReferralQueries = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  queryClient.invalidateQueries({ queryKey: referralKeys.me() });
  queryClient.invalidateQueries({ queryKey: referralKeys.code() });
  queryClient.invalidateQueries({ queryKey: referralKeys.summary() });
  queryClient.invalidateQueries({ queryKey: referralKeys.referrals() });
  queryClient.invalidateQueries({ queryKey: referralKeys.activity() });
  queryClient.invalidateQueries({ queryKey: referralKeys.history() });
  queryClient.invalidateQueries({ queryKey: referralKeys.earnings() });
};

export const useWithdrawReferralEarnings = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ReferralWithdrawalPayload) =>
      withdrawReferralEarnings(payload),
    onSuccess: (data) => {
      invalidateReferralQueries(queryClient);
      toast.success(
        `Done! ${data.amount.toLocaleString('en-NG', {
          style: 'currency',
          currency: 'NGN',
          currencyDisplay: 'narrowSymbol',
        })} is on its way to your account. You earned this.`
      );
      onSuccess?.();
    },
  });
};

export const useApplyReferralSubscriptionCredit = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ReferralSubscriptionCreditPayload) =>
      applyReferralSubscriptionCredit(payload),
    onSuccess: (data) => {
      invalidateReferralQueries(queryClient);
      toast.success(
        `Your subscription is covered. That’s ${data.appliedAmount.toLocaleString(
          'en-NG',
          {
            style: 'currency',
            currency: 'NGN',
            currencyDisplay: 'narrowSymbol',
          }
        )} you didn’t have to pay out of pocket.`
      );
      onSuccess?.();
    },
  });
};
